import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TREES, STATUS_LABEL, STATUS_COLOR, TYPE_ICON_RI, treeIconRi } from '../demo2Data';
import { useDemo2Ctx } from '../Demo2Context';

const TABS = ['기본정보', '수분 이력', '작업 이력', '사진'];

function MoistChartSvg({ base }: { base: number }) {
    const w = 820, h = 220, pad = 34;
    const pts: number[] = [];
    for (let i = 0; i < 30; i++) {
        const wave = Math.sin(i * 0.7) * 6 + Math.sin(i * 1.3) * 3;
        const trend = base + (i - 15) * 0.3;
        pts.push(Math.max(6, Math.min(96, Math.round(trend + wave))));
    }
    const x = (i: number) => pad + (i / 29) * (w - pad * 2);
    const y = (v: number) => h - 26 - (v / 100) * (h - 46);
    const line = pts.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
    const area = `${line} L${x(29).toFixed(1)},${h - 26} L${x(0).toFixed(1)},${h - 26} Z`;
    const c = base < 45 ? '#DC2626' : base < 70 ? '#D97706' : '#1E8B53';
    return (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
            <line x1={pad} y1={y(55)} x2={w - pad} y2={y(55)} stroke="#1E8B53" strokeWidth={1} strokeDasharray="4 3" opacity={0.5} />
            <text x={w - pad} y={y(55) - 5} textAnchor="end" fontSize={10} fill="#1E8B53">양호 55%</text>
            <line x1={pad} y1={y(40)} x2={w - pad} y2={y(40)} stroke="#DC2626" strokeWidth={1} strokeDasharray="4 3" opacity={0.5} />
            <text x={w - pad} y={y(40) - 5} textAnchor="end" fontSize={10} fill="#DC2626">위험 40%</text>
            <path d={area} fill={c} opacity={0.12} />
            <path d={line} fill="none" stroke={c} strokeWidth={2.2} />
            {pts.map((v, i) => (i % 4 === 0 ? <circle key={i} cx={x(i)} cy={y(v)} r={2.6} fill={c} /> : null))}
            <line x1={pad} y1={h - 26} x2={w - pad} y2={h - 26} stroke="#E5E7EB" />
            <text x={pad} y={h - 8} fontSize={10} fill="#9CA3AF">30일 전</text>
            <text x={w - pad} y={h - 8} fontSize={10} fill="#9CA3AF" textAnchor="end">오늘</text>
        </svg>
    );
}

export default function Demo2TreeDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { workHistory } = useDemo2Ctx();
    const [tab, setTab] = useState(0);

    const t = TREES.find(x => x.id === id) ?? TREES[0];
    const c = STATUS_COLOR[t.status];
    const circumference = 2 * Math.PI * 37;
    const offset = circumference - (t.moist / 100) * circumference;
    const hist = workHistory.filter(h => h.zone === t.zone);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1.5px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/demo2/web/tree-list')}>← 목록</button>
                <div style={{ fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7 }}><i className={treeIconRi(t.species)} /> {t.species} <span className="text-mono text-muted" style={{ fontSize: 12, fontWeight: 600 }}>{t.id} · {t.code}</span></div>
                <span className={`badge badge-${t.status}`}><span className={`badge-dot dot-${t.status}`} />{STATUS_LABEL[t.status]}</span>
                <div style={{ flex: 1 }} />
                <button className="btn btn-primary" onClick={() => navigate('/demo2/web/work-management')}><i className="ri-drop-line" /> 관개 작업지시 생성</button>
            </div>

            <div className="detail-tabs">
                {TABS.map((lbl, i) => (
                    <div key={lbl} className={`detail-tab${tab === i ? ' active' : ''}`} onClick={() => setTab(i)}>{lbl}</div>
                ))}
            </div>

            <div style={{ flex: 1, overflow: 'hidden' }}>
                {tab === 0 && (
                    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
                        <div style={{ maxWidth: 660, display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div className="data-card">
                                <div className="data-card-hd"><span className="data-card-ttl">기본 정보</span><button className="btn btn-secondary btn-sm">편집</button></div>
                                <div className="form-row" style={{ marginBottom: 13 }}>
                                    <div className="form-group"><label className="form-label">수목ID</label><input className="form-input" defaultValue={t.id} readOnly /></div>
                                    <div className="form-group"><label className="form-label">관리 코드</label><input className="form-input" defaultValue={t.code} readOnly /></div>
                                </div>
                                <div className="form-row" style={{ marginBottom: 13 }}>
                                    <div className="form-group"><label className="form-label">수종</label><input className="form-input" defaultValue={t.species} readOnly /></div>
                                    <div className="form-group"><label className="form-label">위치 (관리구역)</label><input className="form-input" defaultValue={t.zoneName} readOnly /></div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group"><label className="form-label">식재일</label><input className="form-input" defaultValue={t.planted} readOnly /></div>
                                    <div className="form-group"><label className="form-label">행정구역</label><input className="form-input" defaultValue={t.district} readOnly /></div>
                                </div>
                            </div>
                            <div className="data-card">
                                <div className="data-card-hd"><span className="data-card-ttl">생육 규격</span></div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div className="metric-item"><div className="metric-lbl">수고</div><div className="metric-val">{t.height}m</div></div>
                                    <div className="metric-item"><div className="metric-lbl">흉고둘레</div><div className="metric-val">{t.girth}cm</div></div>
                                    <div className="metric-item"><div className="metric-lbl">마지막 관개</div><div className="metric-val" style={{ fontSize: 13 }}>{t.lastDate}</div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === 1 && (
                    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
                        <div style={{ maxWidth: 900 }}>
                            <div className="data-card" style={{ marginBottom: 14 }}>
                                <div className="data-card-hd"><span className="data-card-ttl">수분율 추이 (최근 30일)</span></div>
                                <MoistChartSvg base={t.moist} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div className="data-card">
                                    <div className="data-card-hd"><span className="data-card-ttl">현재 수분 상태</span></div>
                                    <div className="gauge-wrap">
                                        <div className="gauge-ring">
                                            <svg viewBox="0 0 84 84">
                                                <circle className="g-bg" cx={42} cy={42} r={37} />
                                                <circle className="g-fill" cx={42} cy={42} r={37} stroke={c} strokeDasharray={circumference} strokeDashoffset={offset} />
                                            </svg>
                                            <div className="gauge-val"><div className="gauge-num" style={{ color: c }}>{t.moist}</div><div className="gauge-unit">%</div></div>
                                        </div>
                                        <div style={{ flex: 1 }}><div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>{STATUS_LABEL[t.status]} 상태</div><div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>권고 수분율 55~75%</div></div>
                                    </div>
                                </div>
                                <div className="data-card">
                                    <div className="data-card-hd"><span className="data-card-ttl">토양 깊이별 수분</span></div>
                                    <div className="soil-layers">
                                        {[{ depth: '0-10cm', pct: Math.max(0, t.moist - 7) }, { depth: '10-20cm', pct: Math.min(99, t.moist + 2) }, { depth: '20-30cm', pct: Math.min(99, t.moist + 8) }].map(row => (
                                            <div key={row.depth} className="soil-row">
                                                <div className="soil-depth">{row.depth}</div>
                                                <div className="soil-bar-bg"><div className="soil-bar-fill" style={{ width: `${row.pct}%`, background: c }} /></div>
                                                <div className="soil-pct">{row.pct}%</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === 2 && (
                    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
                        <div className="text-xs text-muted mt-2" style={{ marginBottom: 14, maxWidth: 720 }}><i className="ri-information-line" /> 개체목 단위가 아닌 소속 관리구역({t.zoneName}) 단위 작업 이력입니다.</div>
                        <div className="timeline" style={{ maxWidth: 720 }}>
                            {hist.length === 0 && (
                                <div className="d2-empty">
                                    <i className="ri-file-list-3-line" />
                                    <div className="d2-empty-txt">등록된 작업 이력이 없습니다</div>
                                </div>
                            )}
                            {hist.map((r, i) => (
                                <div key={i} className={`tl-item fade-up d${i % 5}`}>
                                    <div className="tl-dot"><i className={TYPE_ICON_RI[r.type] || 'ri-file-list-3-line'} /></div>
                                    <div className="tl-body">
                                        <div className="tl-meta">{r.date} · {r.worker}</div>
                                        <div className="tl-card">
                                            <div className="tl-card-top"><span className="tl-card-name">{r.type}</span><span className="badge badge-good">완료</span></div>
                                            <div className="tl-stats">
                                                <div className="tl-stat"><div className="tl-stat-lbl">방법</div><div className="tl-stat-val" style={{ fontSize: 11 }}>{r.method}</div></div>
                                                <div className="tl-stat"><div className="tl-stat-lbl">소요시간</div><div className="tl-stat-val">{r.mins}</div></div>
                                                <div className="tl-stat"><div className="tl-stat-lbl">수분 변화</div><div className="tl-stat-val" style={{ fontSize: 11 }}>{r.before}%→{r.after}%</div></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === 3 && (
                    <div style={{ padding: 20, overflowY: 'auto', height: '100%' }}>
                        <div className="text-xs text-muted" style={{ marginBottom: 14 }}><i className="ri-camera-line" /> 현장 작업자가 모바일 앱에서 업로드한 현장 사진입니다 (데모 이미지 없음)</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 12, maxWidth: 900 }}>
                            {['07-14 관개 전', '07-14 관개 후', '07-09 병해충 점검', '07-01 정기 점검', '06-20 식재 직후', '06-01 등록 당시'].map(lbl => (
                                <div key={lbl} style={{ aspectRatio: '1', background: 'var(--surface2)', border: '1.5px dashed var(--border2)', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    <i className={treeIconRi(t.species)} style={{ fontSize: 24, color: 'var(--text3)' }} /><span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{lbl}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
